import React, { useEffect, useState, useRef } from 'react';
import Wrapper from '../../components/Wrapper';
import { Paper, Typography, Box, Divider, Button, IconButton, Alert, TextField } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import RefreshIcon from '@mui/icons-material/Refresh';
import { shortFunction, rate, employee, input, global } from '../../translate';
import Api from "../../Api";
import { useLocation, useNavigate } from 'react-router-dom';
import FileUploader from '../../components/new_component/FileUploader';
import { toast } from 'react-toastify';
import { REACT_APP_API_URL } from "../../config";
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CallbackNotification from './CallbackNotification';

const CALLBACK_ERROR_KEYS = {
    CALLBACK_VIRTUAL_EXISTS: 'callback_virtual_exists',
    'Virtual already exists': 'callback_virtual_exists',
    CALLBACK_EXISTS: 'callback_exists',
    'Callback already exists': 'callback_exists',
    CALLBACK_NOT_FOUND: 'callback_not_found',
    'Callback not found': 'callback_not_found',
    CALLBACK_IN_USE: 'callback_in_use',
    CALLBACK_DELETE_FAILED: 'callback_delete_failed',
};

const callbackErrorText = (error, langIndex) => {
    const message = error?.response?.data?.message || '';
    const key = CALLBACK_ERROR_KEYS[message];
    if (key && shortFunction[key]?.[langIndex]) return shortFunction[key][langIndex];
    return shortFunction.error?.[langIndex] || 'Error';
};

const { REACT_APP_SERVER } = process.env;
const color = REACT_APP_SERVER === 'ATS' ? '#0b73b9' : '#7ebc0a';
const hoverColor = REACT_APP_SERVER === 'ATS' ? '#095a8f' : '#6aa507';

export default function MusicHold() {
    const langIndex = +localStorage.getItem('atsLang') || 1;
    const location = useLocation();
    const isPreCall = location.pathname.includes('pre_call_announcement');
    const isCallback = location.pathname.replace(/\/$/, '') === '/short_function/callback';
    const [musicHoldFile, setMusicHoldFile] = useState(null);
    const [fileTarget, setFileTarget] = useState('musichold');
    const [fileModalOpen, setFileModalOpen] = useState(false);
    const [fileModalLoading, setFileModalLoading] = useState(false);
    const [files, setFiles] = useState([]);
    const [previousFiles, setPreviousFiles] = useState([]);
    const [callbackData, setCallbackData] = useState(null);
    const [shortNumber, setShortNumber] = useState('');
    const [shortGroups, setShortGroups] = useState([]);
    const navigate = useNavigate();
    const audioRef = useRef(null);
    const playingIdRef = useRef(null);
    const [playingId, setPlayingId] = useState(null);
    const keySuffix = '&key=akNwNXN1U2k4bzdEQ09QU29KMUpYbXFiQTV5c3FmUm1MOUNheW5qWlYwOD0=';
    
    const getAudioSrc = (f) => {
        const fid = f?.fname || f?.id || f?.name;
        return fid ? `${REACT_APP_API_URL}/rest/files/audio?file=${fid}${keySuffix}` : null;
    };
    
    const stopAudio = () => {
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }
        setPlayingId(null);
        playingIdRef.current = null;
    };
    
    useEffect(() => {
        (async () => {
            try {
                if (isCallback) {
                    const [{ data }, groupsResponse] = await Promise.all([
                        Api.getCallback(),
                        Api.groupsGet(1).catch(() => ({ data: {} })),
                    ]);
                    setCallbackData(data?.data || null);
                    setShortNumber(data?.data?.virtual != null && data?.data?.virtual !== '' ? String(data.data.virtual) : '');
                    setShortGroups(groupsResponse?.data?.data || []);
                    setMusicHoldFile(data?.data?.file || null);
                    setFiles([]);
                    setPreviousFiles([]);
                    return;
                }
                const { data } = isPreCall
                    ? await Api.getPreCallAnnouncement()
                    : await Api.fileGet(true);
                setFiles(data?.file || []);
                setPreviousFiles(isPreCall ? (data?.previous || []) : []);
            } catch (e) {
                console.log(e);
            }
        })();
    }, [isPreCall, isCallback]);
    
    useEffect(() => {
        if (musicHoldFile || isCallback) return;
        if (isPreCall) {
            const current = files.reduce(
                (best, row) => (!best || Number(row.id) > Number(best.id) ? row : best),
                null
            );
            if (current) setMusicHoldFile(current);
            return;
        }
        const foundMusicHold = files.find(f => Number(f.mhold) === 1);
        if (foundMusicHold) setMusicHoldFile(foundMusicHold);
    }, [files, musicHoldFile, isPreCall]);
    
    const handleFileSave = async (file) => {
        try {
            setFileModalLoading(true);
            const selected = file || musicHoldFile;
            let fid = selected?.id || selected?.fname;
            if (!fid && selected && selected.size) {
                const payload = {
                    file: selected,
                    name: selected.name || 'file',
                    size: selected.size || 0,
                };
                if (isCallback) {
                    const virtual = String(shortNumber || '').trim();
                    if (!virtual) {
                        toast.error((employee.short_number_label?.[langIndex] || 'Internal number') + ' ' + (input.is_req?.[langIndex] || 'is required'));
                        return;
                    }
                    payload.name = shortFunction.callback?.[langIndex] || 'Callback';
                    const { data: createData } = callbackData
                        ? await Api.editCallback({ ...payload, virtual })
                        : await Api.createCallback({ ...payload, virtual });
                    const { data } = await Api.getCallback();
                    setCallbackData(data?.data || null);
                    setShortNumber(data?.data?.virtual != null && data?.data?.virtual !== '' ? String(data.data.virtual) : virtual);
                    setMusicHoldFile(data?.data?.file || createData?.file || null);
                    toast.success(global.success?.[langIndex] || 'Saved');
                    setFileModalOpen(false);
                    return;
                }
                if (isPreCall) {
                    await Api.createPreCallAnnouncement(payload);
                    const { data } = await Api.getPreCallAnnouncement();
                    const uploaded = (data?.file || []).reduce(
                        (best, row) => (!best || Number(row.id) > Number(best.id) ? row : best),
                        null
                    );
                    setFiles(data?.file || []);
                    setPreviousFiles(data?.previous || []);
                    if (uploaded) setMusicHoldFile(uploaded);
                    toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
                    setFileModalOpen(false);
                    return;
                }
                const { data: createData } = await Api.fileCreate(payload);
                const created = createData?.file?.[createData.file.length - 1];
                if (created) {
                    fid = created.id || created.fname;
                    setMusicHoldFile(created);
                }
            }
            if (!fid) {
                setFileModalOpen(false);
                return;
            }
            if (isPreCall) {
                await Api.editPreCallAnnouncement({ status: 8 }, fid);
                const { data } = await Api.getPreCallAnnouncement();
                setFiles(data?.file || []);
                setPreviousFiles(data?.previous || []);
                toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
                setFileModalOpen(false);
                return;
            }
            await Api.fileEdit(fid, 1, 'musichold');
            const { data } = isPreCall
                ? await Api.getPreCallAnnouncement()
                : await Api.fileGet(true);
            setFiles(data?.file || []);
            setPreviousFiles(isPreCall ? (data?.previous || []) : []);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            setFileModalOpen(false);
        } catch (e) {
            console.log(e);
            if (isCallback) toast.error(callbackErrorText(e, langIndex));
        } finally {
            setFileModalLoading(false);
        }
    };

    const handleGenerateShortNumber = () => {
        const shorts = shortGroups.filter((item) => Number(item.isivr) === 0);
        if (shorts.length > 0) {
            const used = new Set(
                shorts
                    .filter((item) => +item.virtual !== 59999 && +item.virtual !== 568898)
                    .map((item) => Number(item.virtual) || 0)
            );
            let newShortNumber = 1;
            while (used.has(newShortNumber)) newShortNumber++;
            setShortNumber(String(newShortNumber));
        } else {
            setShortNumber('11');
        }
    };

    const handleSave = async () => {
        try {
            setFileModalLoading(true);
            if (isCallback) {
                const virtual = String(shortNumber || '').trim();
                if (!virtual) {
                    toast.error((employee.short_number_label?.[langIndex] || 'Internal number') + ' ' + (input.is_req?.[langIndex] || 'is required'));
                    return;
                }
                if (!callbackData) {
                    toast.error(shortFunction.callback_not_found?.[langIndex] || 'Callback not found');
                    return;
                }
                await Api.editCallback({ virtual });
                const { data } = await Api.getCallback();
                setCallbackData(data?.data || null);
                setShortNumber(data?.data?.virtual != null && data?.data?.virtual !== '' ? String(data.data.virtual) : virtual);
                toast.success(global.success?.[langIndex] || 'Saved');
                return;
            }
            if (isPreCall) {
                if (musicHoldFile?.id) {
                    await Api.editPreCallAnnouncement({ status: 8 }, musicHoldFile.id);
                }
                const { data } = await Api.getPreCallAnnouncement();
                setFiles(data?.file || []);
                setPreviousFiles(data?.previous || []);
                toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
                setFileModalOpen(false);
                return;
            }
            await Api.fileEdit(musicHoldFile.id, 1, 'musichold');
            const { data } = await Api.fileGet(true);
            setFiles(data?.file || []);
            setPreviousFiles(isPreCall ? (data?.previous || []) : []);
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
            setFileModalOpen(false);
        } catch (e) {
            console.log(e);
            if (isCallback) toast.error(callbackErrorText(e, langIndex));
        } finally {
            setFileModalLoading(false);
        }
    }
    const handleDelete = async () => {
        if (!callbackData) return;
        const msg = langIndex === 2 ? 'Удалить обратный звонок?' : langIndex === 3 ? 'Ջնջե՞լ հետադարձ զանգը' : 'Delete callback?';
        if (!window.confirm(msg)) return;
        try {
            setFileModalLoading(true);
            await Api.deleteCallback(callbackData.id);
            stopAudio();
            setCallbackData(null);
            setMusicHoldFile(null);
            setShortNumber('');
            toast.success(global.deleted?.[langIndex] || 'Deleted');
        } catch (e) {
            console.log(e);
            toast.error(callbackErrorText(e, langIndex));
        } finally {
            setFileModalLoading(false);
        }
    };

    const handleDisable = async () => {
        try {
            setFileModalLoading(true);
            const { data } = await Api.disablePreCallAnnouncement();
            setFiles(data?.file || []);
            setPreviousFiles(data?.previous || []);
            setMusicHoldFile(null);
            stopAudio();
            toast.success(shortFunction.operator_save?.[langIndex] || 'Saved');
        } catch (e) {
            console.log(e);
        } finally {
            setFileModalLoading(false);
        }
    }
    const musicHoldLabel = isCallback
        ? (shortFunction.callback?.[langIndex] || 'Callback')
        : isPreCall
            ? (shortFunction.preCall?.[langIndex] || 'Pre-Call Announcement')
            : (shortFunction.musicHold?.[langIndex] || 'Music Hold');
    const pageDescription = isCallback
        ? shortFunction.callbackDesc?.[langIndex]
        : isPreCall
            ? shortFunction.preCallDesc?.[langIndex]
            : (shortFunction.musicHoldDescription?.[langIndex] || 'Music Hold plays background music to callers while they are waiting for an employee or during call transfer.');

    return (
        <Wrapper>
            <div className="tableParent">
                <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '2px solid rgba(0, 0, 0, 0.08)', pb: 2.5, }}>
                        <IconButton
                            size="small"
                            sx={{ color, fontSize: 18, '&:hover': { backgroundColor: `${color}15` } }}
                            onClick={() => navigate(-1)}
                            aria-label="Back"
                        >
                            <ArrowBackIosNewIcon sx={{ fontSize: 20, fontWeight: 700 }} />
                        </IconButton>
                        <Typography variant="h5" sx={{ fontWeight: 700, m: 0 }}>
                            {musicHoldLabel}
                        </Typography>
                    </Box>

                    <Alert
                        icon={<InfoIcon />}
                        severity="success"
                        sx={
                            REACT_APP_SERVER !== 'ATS'
                                ? { mb: 3 }
                                : {
                                    mb: 3,
                                    border: `1px solid ${color}`,
                                    backgroundColor: `${color}10`,
                                    '& .MuiAlert-icon': { color },
                                }
                        }
                    >
                        <Typography variant="body2">
                            {pageDescription}
                        </Typography>
                    </Alert>

                    <Divider />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Paper elevation={0} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, p: 2, minHeight: 160 }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                                {musicHoldLabel}
                            </Typography>
                            {isCallback && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, maxWidth: 420 }}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label={employee.short_number_label?.[langIndex] || 'Internal number'}
                                        placeholder={employee.short_number_placeholder?.[langIndex] || ''}
                                        value={shortNumber}
                                        onChange={(e) => setShortNumber(e.target.value)}
                                        variant="outlined"
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: color },
                                                '&:hover fieldset': { borderColor: hoverColor },
                                                '&.Mui-focused fieldset': { borderColor: color },
                                            },
                                            '& .MuiInputLabel-root.Mui-focused': { color },
                                        }}
                                    />
                                    <IconButton
                                        size="small"
                                        onClick={handleGenerateShortNumber}
                                        sx={{ color, '&:hover': { backgroundColor: `${color}15` } }}
                                        aria-label="Generate short number"
                                    >
                                        <RefreshIcon fontSize="small" />
                                    </IconButton>
                                </Box>
                            )}
                            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                <Button
                                    variant="outlined"
                                    component="label"
                                    sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                >
                                    {musicHoldFile ? musicHoldFile.name : (rate.choose_comp?.[langIndex] || 'Choose from computer')}
                                    <input
                                        type="file"
                                        hidden
                                        accept=".wav,.mp3,audio/wav,audio/mpeg"
                                        onChange={async (ev) => {
                                            const file = ev.target.files?.[0] || null;
                                            if (file) {
                                                const allowed = ['audio/wav', 'audio/x-wav', 'audio/mpeg', 'audio/mp3', 'audio/wave', 'audio/vnd.wave'];
                                                const maxSize = 5 * 1024 * 1024;
                                                if (!allowed.includes(file.type) || file.size > maxSize) {
                                                    toast.error(rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.');
                                                    ev.target.value = '';
                                                    return;
                                                }
                                            }
                                            setMusicHoldFile(file);
                                            if (file) {
                                                setFileTarget('musichold');
                                                await handleFileSave(file);
                                            }
                                        }}
                                    />
                                </Button>
                                {!isCallback && (
                                    <Button
                                        variant="outlined"
                                        sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                        onClick={() => { setFileTarget('musichold'); setFileModalOpen(true); }}
                                    >
                                        {isPreCall
                                            ? (shortFunction.preCallPrevious?.[langIndex] || 'Files used previously')
                                            : (rate.choose_file?.[langIndex] || 'Choose from existing files')}
                                    </Button>
                                )}
                            </Box>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, mb: 2 }}>
                                {rate.file_limit?.[langIndex] || 'Only mp3/wav files up to 5MB are allowed.'}
                            </Typography>
                            {musicHoldFile && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {musicHoldFile ? (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            sx={{ minWidth: 36, padding: '2px 8px', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                                            onClick={async () => {
                                                const fid = musicHoldFile.fname || musicHoldFile.id || musicHoldFile.name;
                                                if (!fid) return;
                                                if (playingId === fid) {
                                                    stopAudio();
                                                    return;
                                                }
                                                try {
                                                    stopAudio();
                                                    const src = getAudioSrc(musicHoldFile);
                                                    if (!src) return;
                                                    const audio = new Audio(src);
                                                    audioRef.current = audio;
                                                    playingIdRef.current = fid;
                                                    setPlayingId(fid);
                                                    audio.onended = () => {
                                                        stopAudio();
                                                    };
                                                    audio.onerror = () => {
                                                        stopAudio();
                                                    };
                                                    await audio.play();
                                                } catch (e) {
                                                    console.log(e);   
                                                    stopAudio();
                                                }
                                            }}
                                        >
                                            {playingId === (musicHoldFile.fname || musicHoldFile.id || musicHoldFile.name) ? '■' : '▶'}
                                        </Button>
                                    ) : null}
                                    <Typography variant="body2" color="text.secondary">{musicHoldFile.name}</Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>

                    {isCallback && (
                        <CallbackNotification embedded disabled={!callbackData} />
                    )}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        {isCallback && callbackData && (
                            <Button
                                variant="outlined"
                                sx={{ textTransform: 'none', color: '#ce405f', borderColor: '#ce405f', mr: 'auto', '&:hover': { borderColor: '#a8324c', color: '#a8324c' } }}
                                onClick={handleDelete}
                            >
                                {global.delete?.[langIndex] || 'Delete'}
                            </Button>
                        )}
                        {isPreCall && files.length > 0 && (
                            <Button
                                variant="outlined"
                                sx={{ textTransform: 'none', color: '#ce405f', borderColor: '#ce405f', mr: 'auto', '&:hover': { borderColor: '#a8324c', color: '#a8324c' } }}
                                onClick={handleDisable}
                            >
                                {shortFunction.preCallDisable?.[langIndex] || 'Disable'}
                            </Button>
                        )}
                        <Button
                            variant="outlined"
                            sx={{ textTransform: 'none', color, borderColor: color, '&:hover': { borderColor: hoverColor, color: hoverColor } }}
                            onClick={() => { 
                                navigate('/short_function');
                                setMusicHoldFile(null);
                            }}
                        >
                            {shortFunction.operator_close?.[langIndex] || 'Cancel'}
                        </Button>
                        <Button
                            variant="contained"
                            sx={{ textTransform: 'none', backgroundColor: color, color: '#fff', '&:hover': { backgroundColor: hoverColor } }}
                            onClick={() => 
                                handleSave()
                            }
                        >
                            {shortFunction.operator_save?.[langIndex] || 'Save'}
                        </Button>
                    </Box>
                </Paper>
                <FileUploader
                    open={fileModalOpen}
                    onClose={() => setFileModalOpen(false)}
                    files={isPreCall ? previousFiles : files}
                    listLabel={isPreCall ? (shortFunction.preCallPrevious?.[langIndex] || 'Files used previously') : undefined}
                    loading={fileModalLoading}
                    target={fileTarget}
                    selectedId={musicHoldFile?.id || musicHoldFile?.fname || musicHoldFile?.name}
                    onUpload={(f) => {
                        setMusicHoldFile(f);
                    }}
                    onSelect={(f) => {
                        setMusicHoldFile(f);
                    }}
                    onSave={handleFileSave}
                />
            </div>
        </Wrapper>
    )
}
